import type { ReactNode } from 'react';
import { PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen } from 'lucide-react';

export interface CollapsibleSidePanelProps {
  /** Which edge of the editor the panel sits on — drives the toggle icon. */
  side: 'left' | 'right';
  collapsed: boolean;
  onToggle: () => void;
  /** Name shown along the rail when collapsed, and in the toggle's label. */
  label: string;
  /** Width/padding of the open panel, e.g. `w-48 p-2`. */
  expandedClassName: string;
  /** Optional heading rendered beside the toggle while the panel is open. */
  header?: ReactNode;
  testId: string;
  toggleTestId: string;
  children: ReactNode;
}

/**
 * A side panel of the floor plan editor that collapses to a narrow rail.
 *
 * The canvas is what the page is for, so both the object library and the
 * properties panel can be folded away to give it the width — without hiding
 * anything: the rail keeps its name and its toggle in place.
 */
export function CollapsibleSidePanel({
  side,
  collapsed,
  onToggle,
  label,
  expandedClassName,
  header,
  testId,
  toggleTestId,
  children,
}: CollapsibleSidePanelProps) {
  const OpenIcon = side === 'left' ? PanelLeftOpen : PanelRightOpen;
  const CloseIcon = side === 'left' ? PanelLeftClose : PanelRightClose;
  const action = `${collapsed ? 'Show' : 'Hide'} ${label.toLowerCase()}`;

  return (
    <div
      className={`bg-white rounded-lg shadow flex-shrink-0 ${
        collapsed ? 'w-9 p-1' : `${expandedClassName} overflow-auto`
      }`}
      data-testid={testId}
    >
      <div
        className={`flex items-center mb-2 ${
          collapsed || !header ? (collapsed ? 'justify-center' : 'justify-end') : 'justify-between'
        }`}
      >
        {!collapsed && header}
        <button
          onClick={onToggle}
          className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded"
          data-testid={toggleTestId}
          aria-label={action}
          title={action}
        >
          {collapsed ? <OpenIcon size={14} /> : <CloseIcon size={14} />}
        </button>
      </div>
      {collapsed ? (
        <div
          className="text-[10px] font-medium text-gray-400 tracking-wide"
          style={{ writingMode: 'vertical-rl' }}
        >
          {label}
        </div>
      ) : (
        children
      )}
    </div>
  );
}
